import React from 'react';
import { Form } from 'react-bootstrap';

import PropTypes from 'prop-types';
import geoDataCache from '../../utils/geoDataCache';

import './AddressLookup.module.css';

class AddressLookup extends React.Component {
  constructor(props) {
    super(props);

    this.state = { 
      address: props.address || '',
      isSelecting: false, // Track if we're programmatically selecting
      geoDataReady: geoDataCache.isReady() // Track if geographic data is loaded
    };
    this.inputRef = React.createRef();
    this.autocomplete = null;
    this.AutocompleteSuggestion = null;
    this.Place = null;
  }

  componentDidMount() {
    // Check if geographic data is loaded, if not wait for it
    this.checkGeoDataStatus();
    
    const initAutocomplete = async () => {
      try {
        if (!window.google || !window.google.maps) {
          // Retry after a short delay if not loaded yet
          setTimeout(initAutocomplete, 100);
          return;
        }

        // Load the places library using the modern loader
        // This satisfies the "New Customers" warning about the script tag
        const { Autocomplete, AutocompleteSuggestion, Place } = await window.google.maps.importLibrary("places");
        const { LatLng, LatLngBounds } = await window.google.maps.importLibrary("core");
        
        // Store classes for manual lookups (needed for browser autofill/blur)
        this.AutocompleteSuggestion = AutocompleteSuggestion;
        this.Place = Place;
        
        // Use the input element that's already rendered by React
        const input = this.inputRef.current;
        if (!input) return;
        
        // Initialize the legacy Autocomplete class on our standard input.
        // We use the legacy class because the new <gmp-place-autocomplete> element 
        // requires "Places API (New)" to be enabled in the Google Cloud Console, 
        // which is currently returning a 403 Forbidden error for this project.
        // Using the legacy class with the modern loader is the recommended middle-ground.
        this.autocomplete = new Autocomplete(input, {
          types: ['address'],
          componentRestrictions: { country: 'ca' },
          bounds: new LatLngBounds(
            new LatLng(48.3, -139.06), // SW corner of BC
            new LatLng(60, -114.03)    // NE corner of BC
          ),
          strictBounds: false
        });

        // Listen for place selection
        this.autocomplete.addListener('place_changed', this.handlePlaceSelect);
        
      } catch (error) {
        console.error('Error initializing Google Maps Autocomplete:', error);
      }
    };

    initAutocomplete();
  }

  componentWillUnmount() {
    if (this.autocomplete) {
      window.google.maps.event.clearInstanceListeners(this.autocomplete);
    }
  }

  checkGeoDataStatus = async () => {
    if (!geoDataCache.isReady()) {
      console.log('AddressLookup: Waiting for geographic data to load...');
      try {
        await geoDataCache.preload();
        this.setState({ geoDataReady: true });
        console.log('AddressLookup: Geographic data ready');
      } catch (error) {
        console.error('AddressLookup: Failed to load geographic data:', error);
      }
    } else {
      this.setState({ geoDataReady: true });
    }
  }

  /**
   * Maps Place data back to legacy format for parent component compatibility
   */
  mapPlaceToLegacyFormat = (place) => {
    // If it's already in legacy format from the widget
    if (place.geometry && place.geometry.location) {
      return place;
    }
    
    // If it's from the new fetchFields API (manual lookup)
    return {
      address_components: (place.addressComponents || []).map(c => ({
        long_name: c.longText,
        short_name: c.shortText,
        types: c.types
      })),
      formatted_address: place.formattedAddress || '',
      geometry: {
        location: {
          lat: () => place.location.lat(),
          lng: () => place.location.lng()
        },
        location_type: 'APPROXIMATE',
        viewport: place.viewport
      },
      place_id: place.id,
      types: place.types || []
    };
  }

  handlePlaceSelect = () => {
    const place = this.autocomplete.getPlace();
    
    if (!place || !place.geometry) {
      console.error('No valid place selected');
      return;
    }

    const address = place.formatted_address || '';
    this.setState({ address });

    // Notify parent that valid address was selected from dropdown
    if (this.props.onAddressChange) {
      this.props.onAddressChange(true);
    }

    this.props.onChange(address, place);
  };

  handleChange = (e) => {
    this.setState({ address: e.target.value });
    if (this.props.onAddressChange) {
      this.props.onAddressChange(false);
    }
  };

  handleAutocompleteSelection = () => {
    if (!this.state.address || this.state.isSelecting) {
      return;
    }

    this.setState({ isSelecting: true });

    try {
      const autocompleteService = new window.google.maps.places.AutocompleteService();
      autocompleteService.getPlacePredictions(
        {
          input: this.state.address,
          componentRestrictions: { country: 'ca' },
        },
        (predictions, status) => {
          if (status !== window.google.maps.places.PlacesServiceStatus.OK || !predictions || predictions.length === 0) {
            this.setState({ isSelecting: false });
            return;
          }
          const placesService = new window.google.maps.places.PlacesService(document.createElement('div'));
          placesService.getDetails(
            {
              placeId: predictions[0].place_id,
              fields: ['address_components', 'formatted_address', 'geometry', 'place_id', 'types']
            },
            (place, status) => {
              if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) {
                this.setState({ isSelecting: false });
                return;
              }
              const address = place.formatted_address || '';
              this.setState({ address, isSelecting: false });

              if (this.props.onAddressChange) {
                this.props.onAddressChange(true);
              }

              this.props.onChange(address, place);
            }
          );
        }
      );
    } catch (error) {
      console.error('Error fetching place suggestions with legacy API:', error);
      this.setState({ isSelecting: false });
    }
  };

  handleBlur = () => {
    if (!this.state.address || this.state.isSelecting) {
      return;
    }

    setTimeout(() => {
      if (this.state.isSelecting || !this.state.address) {
        return;
      }
      this.handleAutocompleteSelection();
    }, 200);
  };

  render() {
    const { geoDataReady } = this.state;
    const placeholder = geoDataReady 
      ? (this.props.placeholder || 'Start typing...') 
      : 'Loading geographic data...';
    
    return (
      <Form.Control
        ref={this.inputRef}
        type='text'
        autoComplete='off'
        name={this.props.name || 'address'}
        required={this.props.required}
        placeholder={placeholder}
        value={this.state.address}
        onChange={this.handleChange}
        onBlur={this.handleBlur}
        disabled={!geoDataReady}
      />
    );
  }
}

AddressLookup.propTypes = {
  onChange: PropTypes.func.isRequired,
  onAddressChange: PropTypes.func,
  address: PropTypes.string,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
};

export default AddressLookup;
